import { useEffect, useMemo, useState } from "react";

import useLocations from "../../data/useLocations";
import useSites from "../../data/useSites";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ISiteSummary } from "../../models/Site";
import classNames from "../../utils/classNames";
import LoadingWheel from "../shared/LoadingWheel";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

interface SiteLocationsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  site?: ISiteSummary;
}

interface PlaceRow {
  id: string;
  name: string;
  siteId?: string | null;
}

type ScopeFilter = "all" | "unassigned" | "thisSite" | "otherSites";

export default function SiteLocationsModal({ open, setOpen, site }: SiteLocationsModalProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Site locations");
  const { sites, assignLocations, unassignLocations } = useSites();
  const { locations } = useLocations();
  const { warehouseLocations } = useWarehouseLocations();

  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<ScopeFilter>("all");
  const [selectedYard, setSelectedYard] = useState<Set<string>>(new Set());
  const [selectedBins, setSelectedBins] = useState<Set<string>>(new Set());

  const siteNames = useMemo(() => {
    const map = new Map<string, string>();
    (sites.data?.data ?? []).forEach((s) => map.set(s.id, s.name));
    return map;
  }, [sites.data]);

  const yardRows: PlaceRow[] = useMemo(
    () => (locations.data?.data ?? []).map((l) => ({ id: l.id, name: l.name, siteId: l.siteId })),
    [locations.data]
  );
  const binRows: PlaceRow[] = useMemo(
    () => (warehouseLocations.data?.data ?? []).map((w) => ({ id: w.id, name: w.displayName, siteId: w.siteId })),
    [warehouseLocations.data]
  );

  const matches = (row: PlaceRow) => {
    if (search && !row.name.toLowerCase().includes(search.toLowerCase())) return false;
    switch (scope) {
      case "unassigned": return !row.siteId;
      case "thisSite": return !!site && row.siteId === site.id;
      case "otherSites": return !!row.siteId && row.siteId !== site?.id;
      default: return true;
    }
  };

  const visibleYard = yardRows.filter(matches).sort((a, b) => a.name.localeCompare(b.name));
  const visibleBins = binRows.filter(matches).sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (open) {
      setSearch("");
      setScope("all");
      setSelectedYard(new Set());
      setSelectedBins(new Set());
    }
  }, [open, site]);

  const toggle = (set: Set<string>, id: string, update: (next: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    update(next);
  };

  const toggleAll = (rows: PlaceRow[], set: Set<string>, update: (next: Set<string>) => void) => {
    const allSelected = rows.length > 0 && rows.every((r) => set.has(r.id));
    const next = new Set(set);
    rows.forEach((r) => allSelected ? next.delete(r.id) : next.add(r.id));
    update(next);
  };

  const selectedCount = selectedYard.size + selectedBins.size;

  const request = () => ({
    locationIds: Array.from(selectedYard),
    warehouseLocationIds: Array.from(selectedBins),
  });

  const afterMutate = {
    onSuccess: () => {
      saveHandlers.onSuccess();
      setSelectedYard(new Set());
      setSelectedBins(new Set());
    },
    onError: saveHandlers.onError,
  };

  const handleAssign = () => {
    if (!site) return;
    assignLocations.mutate({ siteId: site.id, request: request() }, afterMutate);
  };

  const handleUnassign = () => {
    unassignLocations.mutate(request(), afterMutate);
  };

  const isLoading = locations.isLoading || warehouseLocations.isLoading || sites.isLoading;
  const isSaving = assignLocations.isLoading || unassignLocations.isLoading;

  const renderList = (
    title: string,
    rows: PlaceRow[],
    selected: Set<string>,
    update: (next: Set<string>) => void
  ) => {
    const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

    return (
      <div className="flex flex-col min-h-0">
        <div className="flex items-center justify-between pb-2">
          <h2 className="text-sm font-semibold text-gray-800">{title} <span className="font-normal text-gray-500">({rows.length})</span></h2>
          <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              className="border-gray-300 rounded"
              checked={allSelected}
              disabled={rows.length === 0}
              onChange={() => toggleAll(rows, selected, update)}
            />
            Select all shown
          </label>
        </div>
        <ul className="border rounded-md divide-y divide-gray-200 overflow-y-auto max-h-[45vh]">
          {rows.length === 0 && (
            <li className="px-3 py-4 text-sm text-center text-gray-500">Nothing matches</li>
          )}
          {rows.map((row) => {
            const atThisSite = !!site && row.siteId === site.id;
            return (
              <li
                key={row.id}
                onClick={() => toggle(selected, row.id, update)}
                className={classNames(
                  selected.has(row.id) ? "bg-primary-50" : "hover:bg-gray-50",
                  "flex items-center gap-3 px-3 py-2 text-sm cursor-pointer"
                )}
              >
                <input
                  type="checkbox"
                  className="border-gray-300 rounded"
                  checked={selected.has(row.id)}
                  onChange={() => toggle(selected, row.id, update)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="flex-1 truncate">{row.name}</span>
                {row.siteId ? (
                  <span className={classNames(
                    atThisSite ? "bg-primary-50 text-primary-700 ring-primary-600/20" : "bg-gray-50 text-gray-600 ring-gray-500/10",
                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                  )}>
                    {siteNames.get(row.siteId) ?? "Unknown site"}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">No site</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const scopeOptions: { id: ScopeFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "unassigned", label: "No site" },
    { id: "thisSite", label: "At this site" },
    { id: "otherSites", label: "At other sites" },
  ];

  return (
    <Modal width="max-w-5xl" open={open} setOpen={setOpen}>
      <h1 className="text-xl">Assign locations to {site?.name}</h1>
      <p className="mt-1 text-sm text-gray-500">
        Tick the yard locations and warehouse locations that are at this depot. Vehicles and parts take their site from where they are kept, so moving a location moves everything in it.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[30vh]">
          <LoadingWheel />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search locations"
              className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <div className="inline-flex rounded-md shadow-sm">
              {scopeOptions.map((opt, i) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setScope(opt.id)}
                  className={classNames(
                    scope === opt.id ? "bg-primary-600 text-white hover:bg-primary-700" : "bg-white text-gray-700 hover:bg-gray-50",
                    i === 0 ? "rounded-l-md" : "-ml-px",
                    i === scopeOptions.length - 1 ? "rounded-r-md" : "",
                    "relative inline-flex items-center border border-gray-300 px-3 py-2 text-sm font-medium focus:z-10 focus:outline-none"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {renderList("Yard Locations", visibleYard, selectedYard, setSelectedYard)}
            {renderList("Warehouse Locations", visibleBins, selectedBins, setSelectedBins)}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
            <span className="text-sm text-gray-500">{selectedCount} selected</span>
            <div className="flex gap-2">
              <ButtonInput
                label="Unassign selected"
                isSubmit={false}
                onClick={handleUnassign}
                classes={"!bg-white !text-gray-700 !border-gray-300 hover:!bg-gray-50"}
                disabled={selectedCount === 0}
                isLoading={unassignLocations.isLoading}
              />
              <ButtonInput
                label={`Assign selected to ${site?.name ?? "site"}`}
                isSubmit={false}
                onClick={handleAssign}
                classes={undefined}
                disabled={selectedCount === 0 || isSaving}
                isLoading={assignLocations.isLoading}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
